// Sağlık Raporu – KVKK Maskeleme penceresi: await openHealthMask({ file, employeeId }) ile açılır
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Square, Undo2, Redo2, RotateCw, RotateCcw, ZoomIn, ZoomOut, Maximize, ChevronLeft, ChevronRight, Trash2, Loader2, ShieldCheck, Info, MousePointer2, PlayCircle,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { confirmDialog } from '../ConfirmHost';
import { registerHealthMask, detectFileType, uploadMaskedPages, MASK_HOWTO_KEY } from '../../lib/healthMask';
import { PermitHowtoDialog } from '../work-permits/howto/PermitHowtoDialog';
import { MASK_STEPS, MASK_SECTIONS } from '../work-permits/howto/maskSteps';
import { loadSource, getPageCanvas, rotateRects, exportPages } from './maskEngine';
import { MaskCanvas } from './MaskCanvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INFO = 'Bu belge üzerinde paylaşılması gerekmeyen kişisel sağlık bilgilerini siyah alanlarla kapatabilirsiniz. Maskeleme kaydedildikten sonra kapatılan bilgiler geri getirilemez. Lütfen belgeyi kaydetmeden önce kontrol ediniz.';
const EMPTY = { stack: [{ rects: {}, rotation: 0 }], idx: 0 };
const errText = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : e?.message || 'İşlem başarısız');

const Tool = ({ icon: Icon, label, onClick, disabled, active, testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={label} data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}>
    <Icon className="h-4 w-4" /><span className="hidden md:inline">{label}</span>
  </button>
);

const PreviewList = ({ urls }) => (
  <div className="mx-auto flex max-w-3xl flex-col gap-4 py-4" data-testid="mask-preview">
    {urls.map((u, i) => (
      <div key={u} className="bg-white shadow-lg">
        <p className="bg-slate-800 px-3 py-1 text-xs text-white">Sayfa {i + 1} / {urls.length} – kaydedilecek son hali</p>
        <img src={u} alt={`Sayfa ${i + 1}`} className="w-full" />
      </div>
    ))}
  </div>
);

export const HealthMaskHost = () => {
  const [req, setReq] = useState(null);
  const [stage, setStage] = useState('loading');
  const [error, setError] = useState('');
  const [src, setSrc] = useState(null);
  const [ext, setExt] = useState('');
  const [pageIdx, setPageIdx] = useState(0);
  const [page, setPage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [drawMode, setDrawMode] = useState(true);
  const [selected, setSelected] = useState(null);
  const [hist, setHist] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState('');
  const [howto, setHowto] = useState(false);
  const [reminder, setReminder] = useState(false);
  const areaRef = useRef(null);
  const cur = hist.stack[hist.idx];
  const rects = cur.rects[pageIdx] || [];
  const rectCount = Object.values(cur.rects).reduce((n, l) => n + l.length, 0);
  const busy = stage === 'exporting' || stage === 'uploading';

  useEffect(() => registerHealthMask((r) => {
    setReq(r); setStage('loading'); setError(''); setSrc(null); setPage(null); setPageIdx(0);
    setHist(EMPTY); setSelected(null); setPreview(null); setDrawMode(true);
    if (!localStorage.getItem(MASK_HOWTO_KEY)) { localStorage.setItem(MASK_HOWTO_KEY, '1'); setReminder(true); }
  }), []);

  useEffect(() => {
    if (!req) return undefined;
    let alive = true;
    (async () => {
      try {
        const e = await detectFileType(req.file);
        const s = await loadSource(req.file, e);
        const limits = await axios.get(`${API}/settings/doc-page-limits`).then(r => r.data).catch(() => ({}));
        const limit = limits?.employees?.saglik_raporu;
        if (limit && s.numPages > limit) throw new Error(`Bu evrak en fazla ${limit} sayfa olabilir, seçtiğiniz dosya ${s.numPages} sayfa. Lütfen sadece ilgili sayfaları yükleyin.`);
        if (alive) { setExt(e); setSrc(s); setStage('edit'); }
      } catch (e) {
        if (alive) { setError(errText(e)); setStage('error'); }
      }
    })();
    return () => { alive = false; };
  }, [req]);

  const fit = useCallback((pg) => {
    const el = areaRef.current;
    if (!el || !pg) return;
    setZoom(Math.max(0.1, Math.min((el.clientWidth - 32) / pg.width, (el.clientHeight - 32) / pg.height, 2)));
  }, []);

  useEffect(() => {
    if (!src) return;
    let alive = true;
    getPageCanvas(src, pageIdx, src.kind === 'image' ? cur.rotation : 0).then((pg) => { if (alive) { setPage(pg); requestAnimationFrame(() => requestAnimationFrame(() => fit(pg))); } }).catch((e) => { setError(errText(e)); setStage('error'); });
    return () => { alive = false; };
  }, [src, pageIdx, cur.rotation, fit]);

  const commit = (next) => setHist(h => ({ stack: [...h.stack.slice(0, h.idx + 1), next], idx: h.idx + 1 }));
  const setRects = (list) => commit({ ...cur, rects: { ...cur.rects, [pageIdx]: list } });
  const undo = () => { setSelected(null); setHist(h => ({ ...h, idx: Math.max(0, h.idx - 1) })); };
  const redo = () => { setSelected(null); setHist(h => ({ ...h, idx: Math.min(h.stack.length - 1, h.idx + 1) })); };
  const removeSelected = () => { if (selected) { setRects(rects.filter(r => r.id !== selected)); setSelected(null); } };
  const rotate = (dir) => {
    setSelected(null);
    commit({ rotation: (cur.rotation + dir * 90 + 360) % 360, rects: { 0: rotateRects(cur.rects[0] || [], dir, page.width, page.height) } });
  };

  useEffect(() => {
    if (!req || stage !== 'edit') return undefined;
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected) { e.preventDefault(); removeSelected(); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const close = (result) => {
    preview?.urls.forEach(u => URL.revokeObjectURL(u));
    src?.pdf?.destroy();
    req?.resolve(result);
    setReq(null);
  };

  const cancel = async () => {
    if (busy) return;
    if (rectCount && !(await confirmDialog({ title: 'Maskeleme iptal edilsin mi?', description: 'Çizdiğiniz alanlar kaydedilmeyecek ve belge yüklenmeyecek.', confirmLabel: 'İptal Et', cancelLabel: 'Devam Et' }))) return;
    close(null);
  };

  const toPreview = async (mode) => {
    if (mode === 'not_needed' && !(await confirmDialog({
      title: 'Maskeleme gerekmiyor mu?',
      description: 'Bu belgede gizlenmesi gereken (paylaşılması gerekmeyen) kişisel sağlık bilgisi bulunmadığını beyan ediyorum. Belge bu haliyle, metadata temizlenerek yüklenecektir.',
      confirmLabel: 'Beyan Ediyorum', tone: 'primary',
    }))) return;
    setStage('exporting'); setSelected(null);
    try {
      const blobs = await exportPages(src, mode === 'masked' ? cur.rects : {}, cur.rotation, ext, (i, n) => setProgress(`Sayfa ${i} / ${n} hazırlanıyor...`));
      setPreview({ mode, blobs, urls: blobs.map(b => URL.createObjectURL(b)) });
      setStage('preview');
    } catch (e) {
      toast.error(errText(e));
      setStage('edit');
    }
  };

  const backToEdit = () => { preview?.urls.forEach(u => URL.revokeObjectURL(u)); setPreview(null); setStage('edit'); };

  const upload = async () => {
    if (stage === 'uploading') return;
    setStage('uploading');
    try {
      const res = await uploadMaskedPages(req.employeeId, {
        blobs: preview.blobs, sourceType: ext, mode: preview.mode, rectCount: preview.mode === 'masked' ? rectCount : 0,
        originalName: req.file.name, correction: req.correction,
      });
      toast.success(preview.mode === 'masked' ? 'Sağlık raporu maskelenerek yüklendi' : 'Sağlık raporu yüklendi (maskeleme gerekmedi)');
      close(res.data);
    } catch (e) {
      toast.error(errText(e));
      setStage('preview');
    }
  };

  const multi = src?.numPages > 1;
  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent className="flex h-[96vh] w-[98vw] max-w-[1400px] flex-col gap-0 overflow-hidden p-0 [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()} aria-describedby={undefined} data-testid="health-mask-dialog">
        <div className="flex flex-wrap items-center gap-3 border-b bg-slate-900 px-4 py-3 text-white">
          <ShieldCheck className="h-5 w-5 text-emerald-400" />
          <DialogTitle className="text-base font-semibold">Sağlık Raporu – KVKK Maskeleme</DialogTitle>
          {req?.employeeName && <span className="text-xs text-slate-300">{req.employeeName}</span>}
          <span className="ml-auto truncate text-xs text-slate-400" title={req?.file?.name}>{req?.file?.name}</span>
          <button type="button" onClick={() => setHowto(true)} title="Animasyonlu anlatımı izle" data-testid="mask-howto-replay-btn"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-bold text-slate-900 shadow-md transition-[background-color,transform] hover:bg-amber-300 active:scale-95">
            <PlayCircle className="h-4 w-4" />Maskeleme Nasıl Yapacağım?
          </button>
        </div>
        <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="health-mask-info">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /><p>{INFO}</p>
        </div>
        {stage === 'edit' && page && (
          <div className="flex flex-wrap items-center gap-1.5 border-b bg-slate-50 px-3 py-2" data-testid="health-mask-toolbar">
            <Tool icon={Square} label="Siyah Alan Ekle" active={drawMode} onClick={() => setDrawMode(true)} testId="mask-tool-draw" />
            <Tool icon={MousePointer2} label="Seç / Taşı" active={!drawMode} onClick={() => setDrawMode(false)} testId="mask-tool-select" />
            <Tool icon={Trash2} label="Seçili Alanı Sil" disabled={!selected} onClick={removeSelected} testId="mask-tool-delete" />
            <span className="mx-1 h-6 w-px bg-slate-300" />
            <Tool icon={Undo2} label="Geri Al" disabled={hist.idx === 0} onClick={undo} testId="mask-tool-undo" />
            <Tool icon={Redo2} label="Yeniden Yap" disabled={hist.idx >= hist.stack.length - 1} onClick={redo} testId="mask-tool-redo" />
            {src.kind === 'image' && <>
              <span className="mx-1 h-6 w-px bg-slate-300" />
              <Tool icon={RotateCcw} label="Sola Döndür" onClick={() => rotate(-1)} testId="mask-tool-rotate-left" />
              <Tool icon={RotateCw} label="Sağa Döndür" onClick={() => rotate(1)} testId="mask-tool-rotate-right" />
            </>}
            <span className="mx-1 h-6 w-px bg-slate-300" />
            <Tool icon={ZoomOut} label="Uzaklaştır" onClick={() => setZoom(z => Math.max(0.1, z / 1.25))} testId="mask-tool-zoom-out" />
            <span className="w-12 text-center text-xs tabular-nums text-slate-600" data-testid="mask-zoom-value">%{Math.round(zoom * 100)}</span>
            <Tool icon={ZoomIn} label="Yakınlaştır" onClick={() => setZoom(z => Math.min(6, z * 1.25))} testId="mask-tool-zoom-in" />
            <Tool icon={Maximize} label="Ekrana Sığdır" onClick={() => fit(page)} testId="mask-tool-fit" />
            {multi && (
              <div className="ml-auto flex items-center gap-1.5">
                <Tool icon={ChevronLeft} label="Önceki" disabled={pageIdx === 0} onClick={() => { setSelected(null); setPageIdx(i => i - 1); }} testId="mask-page-prev" />
                <span className="text-xs font-semibold text-slate-700" data-testid="mask-page-indicator">Sayfa {pageIdx + 1} / {src.numPages}</span>
                <Tool icon={ChevronRight} label="Sonraki" disabled={pageIdx >= src.numPages - 1} onClick={() => { setSelected(null); setPageIdx(i => i + 1); }} testId="mask-page-next" />
              </div>
            )}
          </div>
        )}
        <div ref={areaRef} className="relative flex-1 overflow-auto bg-slate-300" data-testid="health-mask-area">
          {(stage === 'loading' || stage === 'exporting' || stage === 'uploading' || (stage === 'edit' && !page)) && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-slate-300/80 text-sm text-slate-700" data-testid="health-mask-loading">
              <Loader2 className="h-8 w-8 animate-spin" />
              {stage === 'exporting' ? progress : stage === 'uploading' ? 'Maskelenmiş belge güvenli şekilde yükleniyor...' : 'Belge açılıyor...'}
            </div>
          )}
          {stage === 'error' && <p className="p-10 text-center text-sm font-medium text-rose-700" data-testid="health-mask-error">{error}</p>}
          {stage === 'edit' && page && (
            <div className="flex min-h-full min-w-fit items-center justify-center p-4">
              <MaskCanvas page={page} rects={rects} zoom={zoom} drawMode={drawMode} selectedId={selected} onSelect={setSelected} onCommit={setRects} />
            </div>
          )}
          {(stage === 'preview' || (stage === 'uploading' && preview)) && <PreviewList urls={preview.urls} />}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t bg-white px-4 py-3">
          <span className="mr-auto text-xs text-slate-500" data-testid="mask-rect-count">{stage === 'preview' ? 'Önizleme: kaydedilecek son belge' : `${rectCount} siyah alan`}</span>
          {stage === 'preview' || stage === 'uploading' ? (
            <>
              <Button variant="outline" onClick={backToEdit} disabled={busy} data-testid="mask-back-btn">Düzenlemeye Dön</Button>
              <Button onClick={upload} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mask-upload-btn">
                {stage === 'uploading' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Onayla ve Yükle
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={cancel} disabled={busy} data-testid="mask-cancel-btn">İptal</Button>
              <Button variant="outline" onClick={() => toPreview('not_needed')} disabled={stage !== 'edit' || rectCount > 0} title={rectCount ? 'Çizilmiş alan varken kullanılamaz' : undefined} data-testid="mask-not-needed-btn">Maskeleme Gerekmiyor</Button>
              <Button onClick={() => toPreview('masked')} disabled={stage !== 'edit' || rectCount === 0} className="bg-slate-900 hover:bg-slate-800" data-testid="mask-complete-btn">Maskelemeyi Tamamla ve Yükle</Button>
            </>
          )}
        </div>
      </DialogContent>
      <Dialog open={reminder} onOpenChange={setReminder}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="mask-reminder-dialog">
          <DialogTitle className="flex items-center gap-2 text-lg"><Info className="h-5 w-5 text-amber-500" />Hatırlatma Uyarısı</DialogTitle>
          <p className="text-sm leading-relaxed text-slate-700" data-testid="mask-reminder-text">
            Merhaba, Sağlık Raporunuzu KVKK'ya uygun hale getirmeniz gerekmektedir. Bunun için ekranlarımız size yardımcı olacaktır.
            Eğer nasıl yapacağınızı bilmiyorsanız ekranın sağ tarafında olan <b>"Maskeleme Nasıl Yapacağım?"</b> animasyonumuzu hızlıca izleyebilirsiniz.
          </p>
          <div className="flex justify-center pt-2">
            <Button onClick={() => { setReminder(false); setHowto(true); }} className="bg-amber-400 text-slate-900 hover:bg-amber-300" data-testid="mask-reminder-watch-btn">
              <PlayCircle className="mr-2 h-4 w-4" />Animasyonu İzle
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <PermitHowtoDialog open={howto} onClose={() => setHowto(false)} steps={MASK_STEPS} sections={MASK_SECTIONS}
        title="Sağlık raporu nasıl maskelenir?" description="Önce neyin kapatılacağını, sonra bu ekranda nasıl maskeleme yapılacağını adım adım izleyin."
        buttonLabel="Maskeleme Nasıl Yapacağım?" />
    </Dialog>
  );
};
